import { PaymentRequestPaymentMethodEvent, Stripe } from '@stripe/stripe-js';

import { CreatePaymentIntentApiResponse } from 'app/shared/typings';
import { ThirdPartyPaymentMethodType } from 'app/fan/utils/checkout';

interface ConfirmCardPaymentParams {
  stripe: Stripe;
  paymentMethodEvent: PaymentRequestPaymentMethodEvent;
  paymentIntentResponse: CreatePaymentIntentApiResponse;
  trackAnalyticsEvent: (
    eventName: string,
    properties: object,
    trackOnce?: boolean
  ) => void;
  sharedAnalyticsArgs: {
    is_guest_checkout: boolean;
    checkout_version?: number;
    checkout_id?: string | null;
    city_slug?: string;
    is_visa_validated?: boolean;
    payment_method_type?: ThirdPartyPaymentMethodType;
    event_id?: string;
    user_id?: number;
  };
  isExpressCheckout?: boolean;
}
// https://stripe.com/docs/stripe-js/elements/payment-request-button?client=react#react-complete-payment
export async function completeThePayment({
  stripe,
  paymentMethodEvent,
  paymentIntentResponse,
  trackAnalyticsEvent,
  sharedAnalyticsArgs,
  isExpressCheckout = false,
}: ConfirmCardPaymentParams) {
  const {
    createPaymentIntent: { paymentIntentClientSecret },
  } = paymentIntentResponse;

  // First confirm card payment without handling additional auth actions
  const { error, paymentIntent } = await stripe.confirmCardPayment(
    paymentIntentClientSecret,
    { payment_method: paymentMethodEvent.paymentMethod.id },
    { handleActions: false }
  );

  if (error) {
    const userErrorMessage =
      'Something went wrong while confirming the payment. Try again with a new payment method';

    if (trackAnalyticsEvent) {
      trackAnalyticsEvent('Virtual Wallet Payment failed', {
        ...sharedAnalyticsArgs,
        checkout_version: 2,
        error_message: userErrorMessage,
        details: error,
        is_express_checkout: !!isExpressCheckout,
      });

      isExpressCheckout &&
        trackAnalyticsEvent('Express Checkout Failed', {
          ...sharedAnalyticsArgs,
          reason: 'Stripe Error',
          error_message: userErrorMessage,
          details: error,
        });
    }

    throw new Error(userErrorMessage);
  }

  // Report to the browser that the confirmation was successful, prompting
  // it to close the browser payment method collection interface.
  paymentMethodEvent.complete('success');

  const paymentIntentRequiresAction =
    paymentIntent?.status === 'requires_action' ||
    // @ts-expect-error 'requires_source_action' is not in the type definition but it's a possible value. See https://docs.stripe.com/payments/paymentintents/lifecycle#intent-statuses
    paymentIntent?.status === 'requires_source_action';

  // Check if the PaymentIntent requires any actions and if so let Stripe.js
  if (paymentIntentRequiresAction) {
    if (trackAnalyticsEvent) {
      trackAnalyticsEvent('Virtual Wallet Requires Additional Auth', {
        ...sharedAnalyticsArgs,
        checkout_version: 2,
        is_express_checkout: isExpressCheckout,
      });
    }

    // If additional auth is required, confirm payment again. Stripe js will handle the rest of the flow
    const { error } = await stripe.confirmCardPayment(
      paymentIntentClientSecret
    );

    if (error) {
      const userErrorMessage =
        'Something went wrong while confirming the payment. Try again with a new payment method';

      if (trackAnalyticsEvent) {
        trackAnalyticsEvent('Virtual Wallet Payment failed', {
          ...sharedAnalyticsArgs,
          checkout_version: 2,
          error_message: userErrorMessage,
          details: error,
          is_express_checkout: isExpressCheckout,
        });

        isExpressCheckout &&
          trackAnalyticsEvent('Express Checkout Failed', {
            ...sharedAnalyticsArgs,
            error_message: userErrorMessage,
            reason: 'Stripe Error',
            details: error,
          });
      }

      throw new Error(userErrorMessage);
    }
  }

  return true;
}
